/**
 * <oppie-gallery> — custom PDP gallery for the Oppie Horizon theme.
 *
 * Wraps a Swiper main carousel + thumbnail strip and wires PhotoSwipe v5 for
 * fullscreen zoom of image media only. Variant -> image sync mirrors Horizon's
 * native strategy: on `variant:update` we read the freshly rendered section HTML
 * from the event and replace this element wholesale with the new <oppie-gallery>;
 * the replacement's own connectedCallback re-inits everything on the correctly
 * sorted media. No bespoke media-matching logic lives here.
 *
 * Self-hosted vendor libs only (Swiper bundle + PhotoSwipe v5). All work is wrapped
 * in try/catch so a failure leaves the progressively-enhanced fallback visible.
 */

const SWIPER_WAIT_TIMEOUT_MS = 8000;
const SWIPER_WAIT_INTERVAL_MS = 50;
// Interactions on the gallery that load Swiper right away (if the first image
// has not finished loading yet). See whenReadyToBoot().
const BOOT_EVENTS = ['pointerdown', 'touchstart', 'focusin', 'keydown'];

// Transparent 1x1 GIF: stands in for a parked slide image so it renders as an
// empty box (no alt text, no broken-image icon) and costs no request.
const PARKED_IMAGE_SRC = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const WAKE_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'focusin', 'mouseover'];

/**
 * Load a stylesheet once; resolves when it has loaded (or failed, so a missing
 * file never blocks the lightbox from opening).
 * @param {string | undefined} url
 * @returns {Promise<void>}
 */
function loadStylesheet(url) {
  if (!url) return Promise.resolve();
  window.__oppieStylesheets = window.__oppieStylesheets || new Map();
  const cached = window.__oppieStylesheets.get(url);
  if (cached) return cached;

  const promise = new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => {
      window.__oppieStylesheets.delete(url);
      resolve();
    }, { once: true });
    document.head.appendChild(link);
  });
  window.__oppieStylesheets.set(url, promise);
  return promise;
}

class OppieGallery extends HTMLElement {
  constructor() {
    super();
    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.lightbox = null;
    this.controller = new AbortController();
    this.booted = false;
    this.parkedImages = [];
  }

  connectedCallback() {
    if (this.booted) return;
    this.booted = true;

    // Variant sync — register immediately so it survives even if Swiper init fails.
    try {
      const target = this.closest('.shopify-section, dialog');
      target?.addEventListener('variant:update', this.handleVariantUpdate, {
        signal: this.controller.signal,
      });
    } catch (error) {
      console.error('[oppie-gallery] failed to bind variant:update', error);
    }

    this.boot().catch((error) => {
      console.error('[oppie-gallery] init failed; fallback gallery shown', error);
      // Reveal slides even when init fails.
      this.restoreParkedImages();
      this.classList.remove('oppie-pdp-gallery--pre-init');
    });
  }

  disconnectedCallback() {
    try {
      this.controller.abort();
    } catch (error) {
      /* no-op */
    }
    this.destroyInstances();
  }

  /**
   * Speed (LCP on mobile): slides 2+ sit right beside slide 1, inside the browser's
   * lazy-load margin, so their images (80-90 KB each) would start downloading the
   * moment Swiper reveals them and compete with the first image. Park their
   * src/srcset until the visitor touches the gallery or the page has finished
   * loading, whichever comes first. Pre-init these slides are display:none, so
   * nothing has been requested yet when this runs. Off-screen slides only: no
   * visual change. Touching the gallery (pointerdown/touchstart) restores them
   * before a swipe completes, so the next slide is already on its way.
   */
  parkLaterSlideImages(mainEl) {
    if (document.readyState === 'complete') return;

    const slides = Array.from(mainEl.querySelectorAll('.swiper-slide')).slice(1);
    slides.forEach((slide) => {
      slide.querySelectorAll('img').forEach((img) => {
        if (img.complete && img.currentSrc) return;
        const src = img.getAttribute('src');
        const srcset = img.getAttribute('srcset');
        if (!src && !srcset) return;
        this.parkedImages.push({ img, src, srcset });
        if (srcset !== null) img.removeAttribute('srcset');
        img.setAttribute('src', PARKED_IMAGE_SRC);
      });
    });

    if (this.parkedImages.length === 0) return;

    const signal = this.controller.signal;
    const wake = () => this.restoreParkedImages();
    WAKE_EVENTS.forEach((type) => {
      this.addEventListener(type, wake, { once: true, passive: true, signal });
    });
    window.addEventListener('load', wake, { once: true, signal });
  }

  /** Put parked images back (idempotent). Lazy loading then fetches them as normal. */
  restoreParkedImages() {
    const parked = this.parkedImages;
    if (!parked || parked.length === 0) return;
    this.parkedImages = [];
    parked.forEach(({ img, src, srcset }) => {
      try {
        if (srcset !== null) img.setAttribute('srcset', srcset);
        if (src !== null) img.setAttribute('src', src);
        else img.removeAttribute('src');
      } catch (error) {
        /* no-op */
      }
    });
  }

  destroyInstances() {
    try {
      this.lightbox?.destroy?.();
    } catch (error) {
      /* no-op */
    }
    try {
      this.mainSwiper?.destroy?.(true, true);
    } catch (error) {
      /* no-op */
    }
    try {
      this.thumbsSwiper?.destroy?.(true, true);
    } catch (error) {
      /* no-op */
    }
    this.lightbox = null;
    this.mainSwiper = null;
    this.thumbsSwiper = null;
  }

  /**
   * Speed (LCP on mobile): swiper-bundle.min.js (40 KB) is no longer a <script defer>
   * in the snippet, where it competed with the first image for bandwidth. Boot
   * once the first slide image has loaded, or earlier on the first interaction
   * with the gallery, or at window load, whichever comes first. Until then the
   * gallery stays in its pre-init state, which oppie-gallery.css keeps pixel
   * identical to the booted layout.
   */
  whenReadyToBoot() {
    if (typeof window.Swiper === 'function' || document.readyState === 'complete') {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const signal = this.controller.signal;
      const done = () => resolve();
      BOOT_EVENTS.forEach((type) => {
        this.addEventListener(type, done, { once: true, passive: true, signal });
      });
      window.addEventListener('load', done, { once: true, signal });

      const hero = this.querySelector('[data-oppie-main] .swiper-slide img');
      if (!hero || hero.complete) {
        done();
        return;
      }
      hero.addEventListener('load', done, { once: true, signal });
      hero.addEventListener('error', done, { once: true, signal });
    });
  }

  /** Inject swiper-bundle.min.js once per page (shared across gallery instances). */
  loadSwiperScript() {
    if (typeof window.Swiper === 'function') return;
    const url = this.dataset.swiperUrl;
    if (!url || window.__oppieSwiperScript) return;

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.addEventListener('error', () => {
      // Allow a retry by a later gallery instance (e.g. after a variant swap).
      window.__oppieSwiperScript = null;
      script.remove();
    });
    window.__oppieSwiperScript = script;
    document.head.appendChild(script);
  }

  /** Resolve once window.Swiper is available (defer script ordering safe). */
  waitForSwiper() {
    if (typeof window.Swiper === 'function') return Promise.resolve(window.Swiper);
    this.loadSwiperScript();

    return new Promise((resolve, reject) => {
      const start = Date.now();
      const timer = setInterval(() => {
        if (typeof window.Swiper === 'function') {
          clearInterval(timer);
          resolve(window.Swiper);
          return;
        }
        if (Date.now() - start > SWIPER_WAIT_TIMEOUT_MS) {
          clearInterval(timer);
          reject(new Error('window.Swiper never became available'));
        }
      }, SWIPER_WAIT_INTERVAL_MS);
    });
  }

  async boot() {
    const mainEl = this.querySelector('[data-oppie-main]');
    if (!mainEl) return;

    await this.whenReadyToBoot();
    if (!this.isConnected) return;

    const Swiper = await this.waitForSwiper();
    if (!this.isConnected) return;

    const slideCount = mainEl.querySelectorAll('.swiper-slide').length;

    // Thumbnails (optional — absent for single-media products).
    const thumbsEl = this.querySelector('[data-oppie-thumbs]');
    if (thumbsEl && slideCount > 1) {
      this.thumbsSwiper = new Swiper(thumbsEl, {
        slidesPerView: 'auto',
        spaceBetween: 8,
        freeMode: true,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        // Desktop: let the mouse wheel scroll the horizontal thumbnail strip when
        // the pointer is over it (touch swipe already covers mobile). releaseOnEdges
        // hands scroll back to the page at the strip's start/end so it never traps.
        mousewheel: { forceToAxis: false, releaseOnEdges: true, sensitivity: 1 },
        a11y: { enabled: true },
      });
    }

    // Main carousel. loop is intentionally OFF — looping clones slides, which
    // breaks Horizon's deferred-media / product-model <template> elements.
    // Navigation arrows are intentionally omitted; users move via swipe/drag,
    // the thumbnail strip, or keyboard.
    this.mainSwiper = new Swiper(mainEl, {
      slidesPerView: 1,
      spaceBetween: 12,
      grabCursor: true,
      loop: false,
      autoHeight: true,
      watchSlidesProgress: true,
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: { enabled: true },
      thumbs: this.thumbsSwiper ? { swiper: this.thumbsSwiper } : undefined,
    });

    // Pause media on the slide we're leaving, then auto-play the one we land on
    // (only if it's a video/external_video). Fires on swipe, arrows and thumb clicks.
    this.mainSwiper.on('slideChange', () => {
      this.pauseInactiveMedia();
      this.playActiveMedia();
    });

    // Restore parked images as soon as the slide changes by any route (keyboard,
    // thumbnail, programmatic), in case no wake event fired first.
    this.mainSwiper.on('slideChangeTransitionStart', () => this.restoreParkedImages());

    await this.initPhotoSwipe();

    // Park slides 2+ right before they become visible (see parkLaterSlideImages).
    this.parkLaterSlideImages(mainEl);

    // Booted — drop fallback styling so Swiper layout takes over.
    this.classList.remove('oppie-pdp-gallery--pre-init');
  }

  /** Pause media in every slide except the active one (off-screen video etc.). */
  pauseInactiveMedia() {
    try {
      const activeId = this.mainSwiper?.slides?.[this.mainSwiper.activeIndex]?.dataset?.mediaId;
      const slides = this.querySelectorAll('.oppie-pdp-gallery__slide');
      slides.forEach((slide) => {
        if (slide.dataset.mediaId === activeId) return;
        // Prefer the theme's deferred-media pause API if present.
        const deferred = slide.querySelector('deferred-media');
        if (deferred && typeof deferred.pause === 'function') {
          deferred.pause();
          return;
        }
        slide.querySelectorAll('video').forEach((video) => {
          if (!video.paused) video.pause();
        });
        // Best-effort pause for embedded iframes (YouTube/Vimeo) via postMessage.
        slide.querySelectorAll('iframe').forEach((iframe) => {
          try {
            iframe.contentWindow?.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}',
              '*'
            );
            iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
          } catch (error) {
            /* cross-origin — ignore */
          }
        });
      });
    } catch (error) {
      console.error('[oppie-gallery] pauseInactiveMedia failed', error);
    }
  }

  /**
   * Auto-play the active slide's media when it is a video / external_video.
   * Reveals the deferred-media (which auto-plays the muted <video>/iframe via
   * Horizon's own showDeferredMedia) on first landing, or resumes an already
   * revealed one. Images and 3D models are left untouched.
   */
  playActiveMedia() {
    try {
      const activeSlide = this.mainSwiper?.slides?.[this.mainSwiper.activeIndex];
      if (!activeSlide) return;

      const mediaType = activeSlide.dataset.mediaType;
      if (mediaType !== 'video' && mediaType !== 'external_video') return;

      const deferred = activeSlide.querySelector('deferred-media');
      if (deferred) {
        // Already revealed -> resume; otherwise reveal (which starts playback).
        if (deferred.getAttribute('data-media-loaded')) {
          deferred.playMedia?.();
        } else {
          deferred.showDeferredMedia?.();
        }
        return;
      }

      // Fallback: raw <video> without the deferred-media wrapper.
      const video = activeSlide.querySelector('video');
      if (video) video.play().catch(() => {});
    } catch (error) {
      console.error('[oppie-gallery] playActiveMedia failed', error);
    }
  }

  async initPhotoSwipe() {
    const lightboxUrl = this.dataset.pswpLightboxUrl;
    const pswpUrl = this.dataset.pswpUrl;
    const mainEl = this.querySelector('[data-oppie-main]');
    if (!lightboxUrl || !pswpUrl || !mainEl) return;

    const hasZoomable = mainEl.querySelector('.oppie-pdp-gallery__zoomable');
    if (!hasZoomable) return; // No image media — nothing to zoom.

    const module = await import(lightboxUrl);
    const PhotoSwipeLightbox = module.default || module.PhotoSwipeLightbox;
    if (!PhotoSwipeLightbox) return;

    const cssUrl = this.dataset.pswpCssUrl;
    const lightbox = new PhotoSwipeLightbox({
      gallery: mainEl,
      children: '.oppie-pdp-gallery__zoomable',
      pswpModule: () => import(pswpUrl),
      // photoswipe.css is only needed once the lightbox opens; PhotoSwipe waits
      // for this promise before it renders, so there is no unstyled flash.
      openPromise: () => loadStylesheet(cssUrl),
      bgOpacity: 0.92,
      paddingFn: () => ({ top: 24, bottom: 24, left: 16, right: 16 }),
    });

    // Feed per-item src/dimensions from the data-pswp-* attributes on each slide.
    lightbox.addFilter('domItemData', (itemData, element) => {
      const src = element.dataset.pswpSrc;
      const width = parseInt(element.dataset.pswpWidth, 10);
      const height = parseInt(element.dataset.pswpHeight, 10);
      if (src) {
        itemData.src = src;
        itemData.width = Number.isFinite(width) ? width : undefined;
        itemData.height = Number.isFinite(height) ? height : undefined;
        const img = element.querySelector('img');
        itemData.msrc = img?.currentSrc || img?.src;
        itemData.alt = img?.alt || '';
      }
      return itemData;
    });

    lightbox.init();
    this.lightbox = lightbox;
  }

  handleVariantUpdate = (event) => {
    try {
      const source = event?.detail?.data?.html;
      if (!source) return;
      const fresh = source.querySelector('oppie-gallery');
      if (!fresh) return;
      // Tear down our instances before swapping to avoid leaks; the new element's
      // own connectedCallback re-initialises on the correctly sorted media.
      this.destroyInstances();
      this.replaceWith(fresh);
    } catch (error) {
      console.error('[oppie-gallery] variant update failed', error);
    }
  };
}

if (!customElements.get('oppie-gallery')) {
  customElements.define('oppie-gallery', OppieGallery);
}
